import { authService } from '../../services/auth';
import { adminApi, type ApplicationErrorDto } from '../../services/admin.api';
import { onLocaleChange, t } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { openAdminApplicationErrorDetails } from './admin-error-dialogs';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import { esc } from './admin-shared';
import './rc-admin-errors-view';
import { RcAdminErrorsView } from './rc-admin-errors-view';

const ADMIN_ERROR_EVENTS = ['apply-filters', 'reset-filters', 'open-details', 'delete', 'page'] as const;

export class RcAdminErrors extends HTMLElement {
  private applicationErrors: ApplicationErrorDto[] = [];
  private deletingIds = new Set<string>();
  private isLoading = false;
  private error = '';
  private totalCount = 0;
  private page = 1;
  private pageSize = 50;
  private totalPages = 0;
  private filters = {
    functionName: '',
    path: '',
    method: '',
    userId: '',
    search: '',
    statusCode: '',
    occurredFrom: '',
    occurredTo: '',
  };
  private disposeLocaleChange?: () => void;

  connectedCallback(): void {
    ADMIN_ERROR_EVENTS.forEach((eventName) => this.addEventListener(`admin-errors-${eventName}`, this.handleAdminEvent as EventListener));
    this.render();
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    if (authService.isAuthenticated) {
      void this.loadErrors();
    }
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    ADMIN_ERROR_EVENTS.forEach((eventName) => this.removeEventListener(`admin-errors-${eventName}`, this.handleAdminEvent as EventListener));
  }

  private async loadErrors(): Promise<void> {
    this.isLoading = true;
    this.error = '';
    this.render();

    try {
      const result = await adminApi.getApplicationErrors({
        functionName: this.filters.functionName || undefined,
        path: this.filters.path || undefined,
        method: this.filters.method || undefined,
        userId: this.filters.userId || undefined,
        search: this.filters.search || undefined,
        statusCode: this.filters.statusCode ? Number(this.filters.statusCode) : undefined,
        occurredFrom: this.filters.occurredFrom || undefined,
        occurredTo: this.filters.occurredTo || undefined,
        page: this.page,
        pageSize: this.pageSize,
      });
      this.applicationErrors = result.items;
      this.totalCount = result.totalCount;
      this.page = result.page;
      this.pageSize = result.pageSize;
      this.totalPages = result.totalPages;
    } catch (error: any) {
      this.error = error?.message || t('admin.errors.loadError');
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private render(): void {
    if (!authService.isAuthenticated || !authService.isAdmin) {
      this.innerHTML = `<div class="empty-state">${t('admin.accessDenied')}</div>`;
      return;
    }
    if (this.isLoading) {
      this.innerHTML = `<div class="empty-state">${t('common.loading')}</div>`;
      return;
    }
    if (this.error) {
      this.innerHTML = `<div class="empty-state">${esc(this.error)}</div>`;
      return;
    }

    this.innerHTML = '<rc-admin-errors-view></rc-admin-errors-view>';
    const view = this.querySelector('rc-admin-errors-view') as RcAdminErrorsView | null;
    if (!view) return;
    view.applicationErrors = this.applicationErrors;
    view.totalCount = this.totalCount;
    view.page = this.page;
    view.pageSize = this.pageSize;
    view.totalPages = this.totalPages;
    view.deletingIds = this.deletingIds;
    view.filters = this.filters;
    view.render();
  }

  private handleAdminEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<any>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'admin-errors-apply-filters': {
        const form = detail.form as HTMLFormElement | undefined;
        if (!form) return;
        const fd = new FormData(form);
        this.filters = {
          functionName: String(fd.get('functionName') || '').trim(),
          path: String(fd.get('path') || '').trim(),
          method: String(fd.get('method') || '').trim(),
          userId: String(fd.get('userId') || '').trim(),
          search: String(fd.get('search') || '').trim(),
          statusCode: String(fd.get('statusCode') || '').trim(),
          occurredFrom: String(fd.get('occurredFrom') || '').trim(),
          occurredTo: String(fd.get('occurredTo') || '').trim(),
        };
        this.page = 1;
        await this.loadErrors();
        return;
      }
      case 'admin-errors-reset-filters':
        this.filters = {
          functionName: '',
          path: '',
          method: '',
          userId: '',
          search: '',
          statusCode: '',
          occurredFrom: '',
          occurredTo: '',
        };
        this.page = 1;
        await this.loadErrors();
        return;
      case 'admin-errors-open-details':
        if (typeof detail.index === 'number' && this.applicationErrors[detail.index]) {
          openAdminApplicationErrorDetails(this.applicationErrors[detail.index]);
        }
        return;
      case 'admin-errors-delete':
        if (typeof detail.index === 'number' && this.applicationErrors[detail.index]) {
          await this.handleDelete(this.applicationErrors[detail.index]);
        }
        return;
      case 'admin-errors-page':
        if (!Number.isNaN(detail.page) && detail.page >= 1 && detail.page !== this.page) {
          this.page = detail.page;
          await this.loadErrors();
        }
        return;
      default:
        return;
    }
  };

  private async handleDelete(applicationError: ApplicationErrorDto): Promise<void> {
    if (this.deletingIds.has(applicationError.id)) {
      return;
    }

    if (!await confirmDialog(t('admin.errors.confirmDelete'))) {
      return;
    }

    this.deletingIds.add(applicationError.id);
    this.render();

    try {
      await adminApi.deleteApplicationError(applicationError.id);
      if (this.page > 1 && this.applicationErrors.length === 1) {
        this.page -= 1;
      }
      await this.loadErrors();
      toastService.success(t('admin.errors.deleteSuccess'));
    } catch (error) {
      console.error('Failed to delete application error:', error);
      toastService.error(t('admin.errors.deleteError'));
    } finally {
      this.deletingIds.delete(applicationError.id);
      this.render();
    }
  }
}

if (!customElements.get('rc-admin-errors')) {
  customElements.define('rc-admin-errors', RcAdminErrors);
}
